import React, { useState } from 'react';
import styled from 'styled-components';
import { StyledButton } from '../../../../components/Buttons/DefaultButton/DefaultButton.styles';
import Api from '../../../../api';

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const PopupTitle = styled.h2`
  font-size: 1.5rem;
  color: #0B4598;
  margin-bottom: 10px;
  text-align: center;
`;

const PopupDescription = styled.p`
  font-size: 0.95rem;
  color: #555;
  margin-bottom: 20px;
  text-align: center;
`;

const RadioGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 10px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: #f0f4ff;
  }
  
  &.selected {
    background-color: #e7f3fe;
    border-color: #b3d7f9;
    box-shadow: 0 0 0 1px #b3d7f9;
    font-weight: 500;
  }
  
  input[type="radio"] {
    width: 18px;
    height: 18px;
    margin-top: 2px;
  }
  
  label {
    flex: 1;
    font-size: 0.95rem;
    cursor: pointer;
  }
`;

const RadioDescription = styled.span`
  font-size: 0.8rem;
  color: #666;
  display: block;
  margin-top: 4px;
  font-weight: normal;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

interface JobSeekingStatusPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedUser: any) => void;
}

const JobSeekingStatusPopup: React.FC<JobSeekingStatusPopupProps> = ({ isOpen, onClose, onUpdate }) => {
  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Return null if not open
  if (!isOpen) {
    return null;
  }

  const handleSubmit = async () => {
    if (selectedStatus === null) return;
    
    try {
      setIsSubmitting(true);

      await Api.updateJobSeekingStatus(selectedStatus);
      
      // Get updated hygienist info and update parent state
      const user = await Api.getUser();
      if (user) {
        onUpdate(user);
      } 
      
      // Ensure onClose is called on a slight delay to allow state updates to complete
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      // Silent error handling
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PopupOverlay>
      <PopupContainer>
        <PopupTitle>What's your job-seeking status?</PopupTitle>
        <PopupDescription>
          We're enhancing our Job Board! To help you find great opportunities, please tell us your current job-seeking status.
          This will be visible to dental offices when you choose to share it.
        </PopupDescription>
        
        <RadioGroupContainer>
          <RadioOption className={selectedStatus === 0 ? "selected" : ""} onClick={() => setSelectedStatus(0)}>
            <input
              type="radio"
              id="temping-only"
              name="jobSeekingStatus"
              value="0"
              checked={selectedStatus === 0}
              onChange={() => setSelectedStatus(0)}
            />
            <label htmlFor="temping-only">
              Temping only
              <RadioDescription>We won't send you any notifications about Job Board postings.</RadioDescription>
            </label>
          </RadioOption>
          
          <RadioOption className={selectedStatus === 2 ? "selected" : ""} onClick={() => setSelectedStatus(2)}>
            <input
              type="radio"
              id="open-to-opportunities"
              name="jobSeekingStatus"
              value="2"
              checked={selectedStatus === 2}
              onChange={() => setSelectedStatus(2)}
            />
            <label htmlFor="open-to-opportunities">
              Open to great opportunities
              <RadioDescription>We'll only send you notifications for Job Board postings that are an ideal match for you personally.</RadioDescription>
            </label>
          </RadioOption>

          <RadioOption className={selectedStatus === 1 ? "selected" : ""} onClick={() => setSelectedStatus(1)}>
            <input
              type="radio"
              id="actively-looking"
              name="jobSeekingStatus"
              value="1"
              checked={selectedStatus === 1}
              onChange={() => setSelectedStatus(1)}
            />
            <label htmlFor="actively-looking">
              Actively looking for a position
              <RadioDescription>We'll notify you of all Job Board postings in your area that match for your role and skills.</RadioDescription>
            </label>
          </RadioOption>
        </RadioGroupContainer>
        
        <ButtonContainer>
          <StyledButton
            variant="contained"
            color="primary"
            disabled={selectedStatus === null || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? "Saving..." : "Submit"}
          </StyledButton>
        </ButtonContainer>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default JobSeekingStatusPopup; 